/**
 * Database encryption using Web Crypto API
 *
 * Uses AES-256-GCM with PBKDF2 key derivation for authenticated encryption.
 * File format:
 *   [4 bytes: "QGEN" magic header]
 *   [16 bytes: salt]
 *   [12 bytes: IV]
 *   [N bytes: encrypted data with auth tag]
 */

// ============================================================================
// Constants
// ============================================================================

/** Magic bytes to identify encrypted QuickGrade databases: "QGEN" */
const ENCRYPTED_HEADER = new Uint8Array([0x51, 0x47, 0x45, 0x4e]);

/** Salt length for PBKDF2 key derivation */
const SALT_LENGTH = 16;

/** IV length for AES-GCM (96 bits recommended) */
const IV_LENGTH = 12;

/** Number of PBKDF2 iterations (balance security vs performance) */
const PBKDF2_ITERATIONS = 100000;

/** AES key length in bits */
const AES_KEY_LENGTH = 256;

/** AES-GCM authentication tag length in bytes */
const AUTH_TAG_LENGTH = 16;

// ============================================================================
// Key Derivation
// ============================================================================

/**
 * Derive an AES-256 key from a password using PBKDF2.
 *
 * @param password - User password
 * @param salt - Random salt (16 bytes)
 * @returns CryptoKey for AES-GCM encryption/decryption
 */
export async function deriveKey(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  // Import password as raw key material
  const passwordKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    "PBKDF2",
    false,
    ["deriveBits", "deriveKey"]
  );

  // Derive AES key using PBKDF2
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer.slice(0) as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    passwordKey,
    { name: "AES-GCM", length: AES_KEY_LENGTH },
    false,
    ["encrypt", "decrypt"]
  );
}

// ============================================================================
// Encryption
// ============================================================================

/**
 * Encrypt database bytes with a password.
 *
 * @param data - Raw database bytes
 * @param password - User password
 * @returns Encrypted data with header, salt, and IV prepended
 */
export async function encryptDatabase(
  data: Uint8Array,
  password: string
): Promise<Uint8Array> {
  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  // Derive encryption key
  const key = await deriveKey(password, salt);

  // Encrypt data with AES-GCM (includes authentication tag)
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    data.buffer.slice(0) as ArrayBuffer
  );

  const encryptedData = new Uint8Array(encryptedBuffer);

  // Combine: header + salt + iv + encrypted data
  const result = new Uint8Array(
    ENCRYPTED_HEADER.length + SALT_LENGTH + IV_LENGTH + encryptedData.length
  );

  let offset = 0;
  result.set(ENCRYPTED_HEADER, offset);
  offset += ENCRYPTED_HEADER.length;
  result.set(salt, offset);
  offset += SALT_LENGTH;
  result.set(iv, offset);
  offset += IV_LENGTH;
  result.set(encryptedData, offset);

  return result;
}

// ============================================================================
// Decryption
// ============================================================================

/**
 * Decrypt encrypted database bytes with a password.
 *
 * @param encryptedData - Encrypted data (with header, salt, IV)
 * @param password - User password
 * @returns Decrypted database bytes
 * @throws Error if decryption fails (wrong password or corrupted data)
 */
export async function decryptDatabase(
  encryptedData: Uint8Array,
  password: string
): Promise<Uint8Array> {
  // Verify minimum length
  const minLength = ENCRYPTED_HEADER.length + SALT_LENGTH + IV_LENGTH + AUTH_TAG_LENGTH;
  if (encryptedData.length < minLength) {
    throw new Error("Encrypted data is too short or corrupted");
  }

  // Verify header
  if (!isEncryptedDatabase(encryptedData)) {
    throw new Error("Not an encrypted QuickGrade database");
  }

  // Extract salt, IV, and encrypted content
  let offset = ENCRYPTED_HEADER.length;

  const salt = encryptedData.slice(offset, offset + SALT_LENGTH);
  offset += SALT_LENGTH;

  const iv = encryptedData.slice(offset, offset + IV_LENGTH);
  offset += IV_LENGTH;

  const ciphertext = encryptedData.slice(offset);

  // Derive decryption key
  const key = await deriveKey(password, salt);

  try {
    // Decrypt with AES-GCM (verifies authentication tag)
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ciphertext
    );

    return new Uint8Array(decryptedBuffer);
  } catch (error) {
    // AES-GCM will throw if authentication fails (wrong password)
    throw new Error("Incorrect password or corrupted data");
  }
}

// ============================================================================
// Detection
// ============================================================================

/**
 * Check if data appears to be an encrypted QuickGrade database.
 *
 * @param data - Data to check
 * @returns true if data starts with the encrypted header magic bytes
 */
export function isEncryptedDatabase(data: Uint8Array): boolean {
  if (data.length < ENCRYPTED_HEADER.length) {
    return false;
  }

  for (let i = 0; i < ENCRYPTED_HEADER.length; i++) {
    if (data[i] !== ENCRYPTED_HEADER[i]) {
      return false;
    }
  }

  return true;
}

